import { useCallback, useEffect, useRef, useState } from "react";

/* 可拖动 / 可拉伸 / 可最大化的悬浮窗口几何：按 key 记住位置与大小（localStorage）；拖动标题栏移动、拖动边缘 / 角落改大小，始终限制在视口内 */
const PAD = 8;
const load = (k) => { try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; } };
const save = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };
const clampRect = (r, min) => {
  const W = window.innerWidth, H = window.innerHeight;
  const w = Math.max(min.w, Math.min(Number(r.w) || min.w, W - PAD * 2));
  const h = Math.max(min.h, Math.min(Number(r.h) || min.h, H - PAD * 2));
  return { w, h, x: Math.max(PAD, Math.min(Number(r.x) || PAD, W - w - PAD)), y: Math.max(PAD, Math.min(Number(r.y) || PAD, H - h - PAD)) };
};

export function useFloatingWindow(key, { defaultRect, min = { w: 320, h: 360 }, enabled = true }) {
  const [rect, setRect] = useState(null);
  const [maxed, setMaxed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const ref = useRef(null);
  const minRef = useRef(min);
  minRef.current = min;
  const defRef = useRef(defaultRect);
  defRef.current = defaultRect;
  const commit = (r) => { ref.current = r; setRect(r); };

  useEffect(() => {
    if (!enabled) return undefined;
    commit(clampRect(load(`${key}_rect`) || defRef.current(), minRef.current));
    setMaxed(!!load(`${key}_max`));
    const onResize = () => { if (ref.current) commit(clampRect(ref.current, minRef.current)); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [key, enabled]); // eslint-disable-line

  const track = useCallback((e, onMove) => {
    if (e.button !== 0 || !ref.current) return;
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY, start = { ...ref.current };
    setDragging(true);
    const move = (ev) => onMove(ev.clientX - sx, ev.clientY - sy, start);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up); setDragging(false); if (ref.current) save(`${key}_rect`, ref.current); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }, [key]);

  const onDragStart = useCallback((e) => { if (!maxed) track(e, (dx, dy, s) => commit(clampRect({ ...s, x: s.x + dx, y: s.y + dy }, minRef.current))); }, [maxed, track]);
  const onResizeStart = useCallback((dir) => (e) => {
    if (maxed) return;
    track(e, (dx, dy, s) => {
      const m = minRef.current;
      let { x, y, w, h } = s;
      if (dir.includes("e")) w = s.w + dx;
      if (dir.includes("s")) h = s.h + dy;
      if (dir.includes("w")) { w = s.w - dx; x = s.x + dx; }
      if (dir.includes("n")) { h = s.h - dy; y = s.y + dy; }
      if (w < m.w) { if (dir.includes("w")) x = s.x + s.w - m.w; w = m.w; }
      if (h < m.h) { if (dir.includes("n")) y = s.y + s.h - m.h; h = m.h; }
      commit(clampRect({ x, y, w, h }, m));
    });
  }, [maxed, track]);
  const toggleMax = useCallback(() => setMaxed((m) => { save(`${key}_max`, !m); return !m; }), [key]);
  const reset = useCallback(() => { localStorage.removeItem(`${key}_rect`); localStorage.removeItem(`${key}_max`); setMaxed(false); commit(clampRect(defRef.current(), minRef.current)); }, [key]); // eslint-disable-line

  const style = !rect ? null : maxed ? { left: PAD, top: PAD, width: window.innerWidth - PAD * 2, height: window.innerHeight - PAD * 2 } : { left: rect.x, top: rect.y, width: rect.w, height: rect.h };
  return { rect, style, maxed, dragging, onDragStart, onResizeStart, toggleMax, reset };
}
